import { z } from 'zod'

import { type ContactDraft } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { type ContactDirectoryEntry } from '#app/routes/mocra+/_form+/contact-directory.ts'

const STRING_WITH_WHITESPACE_REGEX = /\s+/g
const EMAIL_SCHEMA = z.string().email()

export const ContactReferenceSchema = z.object({
	contactId: z
		.string({
			required_error: 'Select a contact.',
		})
		.min(1, 'Select a contact.'),
	fallbackName: z.string().optional(),
	fallbackEmail: z.string().email().optional(),
	fallbackTitle: z.string().optional(),
	fallbackPhone: z.string().optional(),
})

export type ContactReference = z.infer<typeof ContactReferenceSchema>

export type ContactDisplay = {
	readonly contactId: string
	readonly name: string
	readonly email: string
	readonly title: string
	readonly phone: string
}

type CreateDisplayOptions = {
	readonly defaultName?: string
	readonly defaultTitle?: string
}

type DraftOptions = {
	readonly defaultTitle?: string
}

export function createEmptyContactReference(): ContactReference {
	return {
		contactId: '',
	}
}

export function normalizeContactReference(
	reference: ContactReference,
): ContactReference {
	const contactId = reference.contactId.trim()
	const fallbackName = toOptionalString(reference.fallbackName)
	const fallbackEmailCandidate = toOptionalString(reference.fallbackEmail)
	const fallbackEmail =
		fallbackEmailCandidate &&
		EMAIL_SCHEMA.safeParse(fallbackEmailCandidate).success
			? fallbackEmailCandidate
			: undefined
	const fallbackTitle = toOptionalString(reference.fallbackTitle)
	const fallbackPhone = toOptionalString(reference.fallbackPhone)

	return {
		contactId,
		...(fallbackName ? { fallbackName } : {}),
		...(fallbackEmail ? { fallbackEmail } : {}),
		...(fallbackTitle ? { fallbackTitle } : {}),
		...(fallbackPhone ? { fallbackPhone } : {}),
	}
}

export function contactDraftToReference(
	draft: ContactDraft,
	options: DraftOptions = {},
): ContactReference {
	const contactId = toOptionalString(draft.id)
	if (!contactId) {
		throw new Error('Contact assignments require a stable contact id.')
	}
	return normalizeContactReference({
		contactId,
		fallbackName: sanitizeWhitespace(draft.name),
		fallbackEmail: sanitizeWhitespace(draft.email),
		fallbackTitle:
			sanitizeWhitespace(draft.title) ??
			sanitizeWhitespace(options.defaultTitle),
		fallbackPhone: sanitizeWhitespace(draft.phone),
	})
}

export function referenceToContactDraft(
	reference: ContactReference,
	display: ContactDisplay,
): ContactDraft {
	return {
		id: reference.contactId,
		title: display.title,
		name: display.name,
		email: display.email,
		phone: display.phone,
	}
}

export function resolveContactDisplay(
	reference: ContactReference,
	directory: Map<string, ContactDirectoryEntry>,
	options: CreateDisplayOptions = {},
): ContactDisplay {
	const entry = directory.get(reference.contactId)
	const name =
		entry?.name ??
		reference.fallbackName ??
		reference.fallbackEmail ??
		options.defaultName ??
		'Contact'
	const email = entry?.email ?? reference.fallbackEmail ?? ''
	const title =
		entry?.title ??
		reference.fallbackTitle ??
		options.defaultTitle ??
		(entry?.name ? `${entry.name}` : 'Contact')
	const phone = entry?.phone ?? reference.fallbackPhone ?? ''

	return {
		contactId: reference.contactId,
		name,
		email,
		title,
		phone,
	}
}

function toOptionalString(value?: string | null): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

function sanitizeWhitespace(value?: string | null): string | undefined {
	const trimmed = toOptionalString(value)
	if (!trimmed) return undefined
	return trimmed.replace(STRING_WITH_WHITESPACE_REGEX, ' ')
}
